import { Stack } from '@mui/joy';
import { t } from 'i18next';
import { get } from 'lodash';
import { useMemo } from 'react';

import { LISTBOX_STYLE } from 'contacts/constants';
import { FormField, IAutocompleteProps } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextLine } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { Level } from 'theme/enums';

interface IVendorContactFormFieldProps extends Omit<IAutocompleteProps, 'getLabel'> {
  // Narrowed from `IAutocompleteOption`: this element owns `getOptions`, so it knows what an option
  // is. A surface may still type its callback as the wider option — that direction still assigns.
  getLabel?: (option: ICorporateUserAutocomplete) => string;
  // Alternate users choose between same-named contacts by the AP number the import persists, so
  // their surfaces add it as a last line. Off elsewhere — the number means nothing to them.
  showAccountsPayableId?: boolean;
}

export const VendorContactFormField = ({
  name = 'contact',
  label = t('labelVendorContact'),
  requestPayload: customRequestPayload = {},
  showAccountsPayableId = false,
  ...rest
}: IVendorContactFormFieldProps) => {
  const { control, errors } = useFormContext();

  const requestPayload = useMemo(() => {
    return {
      ...customRequestPayload,
      filters: {
        ...(customRequestPayload?.filters as object),
        userType: CorporateUserType.VENDOR,
      },
    };
  }, [customRequestPayload]);

  // Each surface keeps its own single-line `getLabel` for the SELECTED value; only the dropdown
  // rows are rich.
  const renderOptions = (option: ICorporateUserAutocomplete) => {
    return (
      <Stack sx={{ width: '100%' }}>
        <TextLine level={Level.TITLE_MD} text={option.name || DEFAULT_PLACEHOLDER} />

        {option.companyName && (
          <TextLine label={t('labelCompany')} text={option.companyName} isSubText />
        )}

        {option.email && <TextLine label={t('labelEmail')} text={option.email} isSubText />}

        {showAccountsPayableId && option.billToAddressAccountsPayableId && (
          <TextLine
            label={t('labelAPNumber')}
            text={option.billToAddressAccountsPayableId}
            isSubText
          />
        )}
      </Stack>
    );
  };

  return (
    <FormField
      name={name}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        control,
        error: get(errors, name),
        label,
        getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
        requestPayload,
        renderOptions,
        listboxStyle: LISTBOX_STYLE,
        ...rest,
      }}
    />
  );
};
